// Description: Page to add an email channel
// Updated: 28/08/2025
// Author: Simon Franklin

// React
import React, { useEffect, useState } from "react";

// Material UI Components
import {
  Box,
  Button,
  Divider,
  Typography,
  CircularProgress,
  TextField
} from "@mui/material";

// Components and functions for the project
import StepIndicator from "../../components/DynamicForm/StepIndicator";
import StatusIcon from "../../components/StatusLabel/StatusIcon";
import { useNavigate } from "react-router-dom";
import { ChannelAddEmail } from "../../api/aws/Channels/ChannelAddEmail";
import { notify } from "../../utils/utils";
import CampaignModal from "../../components/Modals/CampaignModal";
import themeColors from "../../components/ThemeColors/ThemeColors";
import { isAuthenticated } from "../../authMiddleware";
import use123zapStore from "../../context/123zapStore";
import ReturnToLastPage from "../../components/ChannelAdd/ReturnToLastPage";

const FormContent = React.memo(
  ({
    currentStep,
    steps,
    name,
    email,
    setName,
    setEmail,
    firstTime,
    success,
    setCurrentStep,
    openModal,
  }) => {
    if (currentStep === 1) {
      return (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: { xs: "20px", sm: "30px" },
            width: "100%",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minWidth: "100%",
              flexDirection: { xs: "column-reverse", sm: "row" },
              gap: { xs: "30px", sm: "0px" },
            }}
          >
            <StepIndicator currentStep={currentStep} steps={steps} />
          </Box>

          <Divider
            sx={{
              width: "100%",
              borderColor: "gray",
              marginBottom: { xs: "0px", sm: "15px" },
            }}
          />

          <Typography
            sx={{
              fontSize: { xs: "16px", sm: "1.125rem" },
              fontWeight: 700,
              color: "#000",
            }}
          >
            {steps[currentStep - 1].title}
          </Typography>

          <Typography
            sx={{
              fontSize: { xs: "12px", sm: "14px" },
              color: "#E923B",
              marginBottom: "19px",
              textAlign: "center",
              maxWidth: { xs: "100%", sm: "80%" },
            }}
          >
            {steps[currentStep - 1].description}
          </Typography>

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "18px",
              width: "100%",
              marginTop: "20px",
            }}
          >
            <Box sx={{ width: { xs: "100%", sm: "50%" }, margin: "0 auto" }}>
              <TextField
                value={name}
                placeholder="Nome de identificação"
                onChange={(e) => setName(e.target.value)}
                sx={{
                  "& .css-1t8l2tu-MuiInputBase-input-MuiOutlinedInput-input": {
                    padding: "12px",
                  },
                  borderRadius: "6px",
                  width: "100%",
                }}
              />
            </Box>
            <Box sx={{ width: { xs: "100%", sm: "50%" }, margin: "0 auto" }}>
              <TextField
                value={email}
                placeholder="Seu email"
                onChange={(e) => setEmail(e.target.value)}
                sx={{
                  "& .css-1t8l2tu-MuiInputBase-input-MuiOutlinedInput-input": {
                    padding: "12px",
                  },
                  borderRadius: "6px",
                  width: "100%",
                }}
              />
            </Box>
          </Box>
        </Box>
      );
    } else if (
      (currentStep === 2 && firstTime > 0) ||
      (currentStep === 3 && firstTime > 0)
    ) {
      return (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: { xs: "20px", sm: "30px" },
            width: "100%",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minWidth: "100%",
              flexDirection: { xs: "column-reverse", sm: "row" },
              gap: { xs: "30px", sm: "0px" },
            }}
          >
            <StepIndicator currentStep={currentStep} steps={steps} />
          </Box>

          <Divider
            sx={{
              width: "100%",
              borderColor: "gray",
              marginBottom: { xs: "0px", sm: "15px" },
            }}
          />

          <Typography
            sx={{
              fontSize: { xs: "16px", sm: "1.125rem" },
              fontWeight: 700,
              color: "#000",
            }}
          >
            Aguarde um instante! 😉
          </Typography>

          <Typography
            sx={{
              fontSize: { xs: "12px", sm: "14px" },
              color: "#E923B",
              marginBottom: "19px",
              textAlign: "center",
              maxWidth: { xs: "100%", sm: "80%" },
            }}
          >
            Estamos processando os dados para a criação do canal de E-mail
          </Typography>

          <Box
            sx={{
              border: "2px solid #F5F3FF",
              borderRadius: "50%", // Garante que seja um círculo
              height: "200px", // Define o tamanho fixo para o círculo
              width: "200px", // Define a mesma largura para o círculo
              display: "flex",
              flexDirection: "column", // Organiza os itens em coluna
              justifyContent: "center", // Centraliza verticalmente
              alignItems: "center", // Centraliza horizontalmente
              textAlign: "center", // Alinhamento do texto centralizado
              position: "relative",
            }}
          >
            <CircularProgress
              sx={{
                position: "absolute", // Faz com que o progresso circular fique em cima do Box
                color: "#F5F3FF", // Cor do progresso circular
              }}
              size={200} // Tamanho do círculo
              thickness={1} // Espessura do progresso
            />
          </Box>
        </Box>
      );
    } else if (currentStep === 3 && firstTime === 0) {
      return (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: { xs: "20px", sm: "30px" },
            width: "100%",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              minWidth: "100%",
              flexDirection: { xs: "column-reverse", sm: "row" },
              gap: { xs: "30px", sm: "0px" },
            }}
          >
            <Typography
              sx={{
                fontSize: { xs: "16px", sm: "1.125rem" },
                fontWeight: 700,
                color: "#000",
              }}
            >
              {steps[currentStep - 1].title}
            </Typography>
            <StepIndicator currentStep={currentStep} steps={steps} />
          </Box>

          <Divider
            sx={{
              width: "100%",
              borderColor: "gray",
              marginBottom: { xs: "0px", sm: "15px" },
            }}
          />

          <Typography
            sx={{
              fontSize: { xs: "12px", sm: "14px" },
              color: "#E923B",
              marginBottom: "19px",
              textAlign: "center",
              maxWidth: { xs: "100%", sm: "80%" },
            }}
          >
            {steps[currentStep - 1].description}
          </Typography>
        </Box>
      );
    } else if (currentStep === 4) {
      return (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: { xs: "20px", sm: "30px" },
            width: "100%",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              minWidth: "100%",
              flexDirection: { xs: "column-reverse", sm: "row" },
              gap: { xs: "30px", sm: "0px" },
            }}
          >
            <Typography
              sx={{
                fontSize: { xs: "16px", sm: "1.125rem" },
                fontWeight: 700,
                color: "#000",
              }}
            >
              {steps[currentStep - 1].title}
            </Typography>
            <StepIndicator currentStep={currentStep} steps={steps} />
          </Box>

          <Divider
            sx={{
              width: "100%",
              borderColor: "gray",
              marginBottom: { xs: "0px", sm: "15px" },
            }}
          />

          <Typography
            sx={{
              fontSize: { xs: "12px", sm: "14px" },
              color: "#E923B",
              marginBottom: "19px",
              textAlign: "center",
              maxWidth: { xs: "100%", sm: "80%" },
            }}
          >
            {steps[currentStep - 1].description}
          </Typography>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              gap: "20px",
            }}
          >
            <StatusIcon
              color={"red"}
              status={success ? "emoji01" : "emoji10"}
              height={"80px"}
              width={"80px"}
            />
            <StatusIcon
              color={"red"}
              status={success ? "emoji01" : "emoji10"}
              height={"80px"}
              width={"80px"}
            />
            <StatusIcon
              color={"red"}
              status={success ? "emoji01" : "emoji10"}
              height={"80px"}
              width={"80px"}
            />
          </Box>

          {success ? (
            <Button
              variant="contained"
              sx={{
                padding: { xs: "16px 20px", sm: "10px 25px" },
                width: "100%", // Faz o botão ocupar toda a largura
                maxWidth: "400px", // Define uma largura máxima opcional
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                borderRadius: "6px",
                fontSize: "15px",
              }}
              onClick={openModal}
            >
              Verificar meus canais
            </Button>
          ) : (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                gap: { xs: "10px", sm: "10px" },
                marginTop: { xs: "30px", sm: "20px" },
                flexDirection: {
                  xs: "column", // Empilha os botões em telas menores
                  sm: "row", // Alinha os botões na horizontal em telas maiores
                },
                width: "100%", // Faz os botões ocuparem toda a largura
              }}
            >
              <Button
                variant="contained"
                sx={{
                  padding: { xs: "16px 20px", sm: "10px 25px" },
                  width: "100%", // Faz o botão ocupar toda a largura
                  maxWidth: "400px", // Define uma largura máxima opcional
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  borderRadius: "6px",
                  fontSize: "15px",
                }}
              >
                Falar com o suporte
              </Button>
              <Button
                variant="contained"
                sx={{
                  padding: { xs: "16px 20px", sm: "10px 25px" },
                  width: "100%", // Faz o botão ocupar toda a largura
                  maxWidth: "400px", // Define uma largura máxima opcional
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  borderRadius: "6px",
                  fontSize: "15px",
                  backgroundColor: "#300D68",
                }}
                onClick={() => setCurrentStep(3)}
              >
                Tentar novamente
              </Button>
            </Box>
          )}
        </Box>
      );
    }

    return null;
  }
);

const ChannelPageAddEmail = () => {
  const [firstTime, setFirstTime] = useState(10);
  const [currentStep, setCurrentStep] = useState(1);
  const [success, setSuccess] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [verify, setVerify] = useState(false);
  const [instance, setInstance] = useState("");
  const creatingCampaignWithHelpMode =
    JSON.parse(sessionStorage.getItem("creating_campaign")) || null;
  const [openModalCampaign, setOpenModalCampaign] = useState(false);
  const userAuthenticated = use123zapStore((state) => state.userAuthenticated);
  const setAlreadyFetchChannels = use123zapStore(
    (state) => state.setAlreadyFetchChannels
  );
  const userInfo = use123zapStore((state) => state.userInfo);
  const setUserInfo = use123zapStore((state) => state.setUserInfo);
  const planoAtual = use123zapStore((state) => state.planoAtual);

  const navigate = useNavigate();

  useEffect(() => {
    if (userAuthenticated && Object.keys(userInfo).length === 0) {
      const { decryptedData } = isAuthenticated();
      setUserInfo(decryptedData);
    }
  }, [userAuthenticated]);

  useEffect(() => {
    if (planoAtual && planoAtual !== "UltraZap") {
      navigate("/acesso-restrito");
    }
  }, [planoAtual]);

  const HandleChannels = () => {
    creatingCampaignWithHelpMode === true
      ? navigate("/campanhas/adicionar")
      : navigate("/canais");
  };

  const steps = [
    {
      stepNumber: 1,
      title: "Vamos integrar seu E-mail?",
      description: (
        <span>
          Informe um nome para <strong>identificar</strong> o email dentro{" "}
          <br /> da plataforma, e o endereço de <strong>email</strong> que
          deseja integrar. <br /> Você receberá um email de confirmação para
          validar a integração.
        </span>
      ),
    },
    {
      stepNumber: 2,
      title: "",
      description: <span></span>,
    },
    {
      stepNumber: 3,
      title: "Hora de verificar seu email",
      description: (
        <span>
          Agora que o email foi adicionado, vá na sua caixa de entrada e clique
          no link da AWS que foi enviado para você.
        </span>
      ),
    },
    {
      stepNumber: 4,
      title: success ? "Email adicionado com sucesso!" : "Ops! Algo deu errado",
      description: success ? (
        <span>
          Agora você pode começar a enviar Email para seus clientes. Clique no
          botão abaixo para verificar seus canais.
        </span>
      ) : (
        <span>
          Parece que houve um problema ao adicionar o seu Email. Por favor,
          tente novamente mais tarde ou entre em contato com o suporte para
          obter ajuda.
        </span>
      ),
    },
  ];

  const canMoveForward = () => {
    if (currentStep === 1 && name !== "" && email !== "") {
      return true;
    } else if (currentStep === 3 && firstTime === 0) {
      return true;
    }

    notify("Error", "Preencha todos os campos", true);
    return false;
  };

  const handleAdvance = async () => {
    if (currentStep === 1) {
      setCurrentStep(2);

      await ChannelAddEmail({ email, name }).then((response) => {
        setInstance(response.instance);
        setFirstTime(0);
      });
    } else if (currentStep === 3) {
      setFirstTime(1);

      const token = localStorage.getItem("jwt_token");
      const clientId = localStorage.getItem("idToken");

      await fetch(
        `${process.env.REACT_APP_BACKEND_AWS_URL}/channels/email/verify-identity`,
        {
          method: "POST",
          body: JSON.stringify({ code: instance }),
          headers: {
            "Content-Type": "application/json",
            Authorization: `${token}`,
            Client_ID: `${clientId}`,
          },
        }
      )
        .then((response) => response.json())
        .then((response) => {
          console.log(response);
          if (response.info === "Email already verified") {
            setSuccess(true);
            setAlreadyFetchChannels(false);
          } else {
            setSuccess(false);
          }

          setVerify(true);
          setFirstTime(0);
        });
    }
  };

  useEffect(() => {
    if (firstTime === 0 && currentStep === 2) {
      setCurrentStep(3);
    }
    if (
      firstTime === 0 &&
      currentStep === 3 &&
      success === true &&
      verify === true
    ) {
      setCurrentStep(4);
    }
    if (
      firstTime === 0 &&
      currentStep === 3 &&
      success === false &&
      verify === true
    ) {
      notify("Alert", "Seu email ainda não foi verificado", true);
    }
  }, [firstTime, currentStep]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        minHeight: { xs: "100vh", sm: "100vh" },
        backgroundColor: { xs: "#fff", sm: "#F1F5F9" },
      }}
    >
      <ReturnToLastPage />
      <Box
        sx={{
          minWidth: "100%",
          minHeight: "100%",
          display: "flex",
          flexDirection: "column",
          opacity: 0.8,
          flex: 1,
          gap: "15px",
        }}
      >
        <Box
          sx={{
            flex: 1,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            width: "100%",
          }}
        >
          <Box
            id="form"
            sx={{
              background: "#FFF",
              minHeight: {
                xs: "100vh", // Ocupa a altura inteira em telas pequenas
                sm: "100%", // Altura personalizada em telas maiores
                md: "583.2px",
                lg: "583.2px",
                xl: "583.2px",
              },
              minWidth: {
                xs: "100%", // Ocupa a largura inteira em telas pequenas
                sm: "100%", // Largura personalizada em telas maiores
                md: "736.2px",
                lg: "736.2px",
                xl: "736.2px",
              },
              maxWidth: {
                xs: "100%", // Ocupa a largura inteira em telas pequenas
                sm: "100%", // Largura personalizada em telas maiores
                md: "736.2px",
                lg: "736.2px",
                xl: "736.2px",
              },
              padding: "30px",
              borderRadius: {
                xs: "0", // Sem borda arredondada em telas pequenas
                sm: ".5rem", // Bordas arredondadas em telas médias ou maiores
              },
              boxShadow: {
                xs: "none", // Sem sombra em telas pequenas
                sm: "0 2px 4px -1px rgba(0, 0, 0, 0.1)", // Sombra em telas maiores
              },
              display: "flex",
              flexDirection: "column",
              justifyContent: {
                xs: "flex-start",
                sm: "flex-start",
                md: "space-between",
                lg: "space-between",
              },
              height: "100%", // Faz com que o form ocupe toda a altura disponível
            }}
          >
            <FormContent
              currentStep={currentStep}
              steps={steps}
              name={name}
              setName={setName}
              email={email}
              setEmail={setEmail}
              firstTime={firstTime}
              success={success}
              openModal={() =>
                creatingCampaignWithHelpMode === true
                  ? setOpenModalCampaign(true)
                  : navigate("/canais")
              }
            />
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                gap: { xs: "0px", sm: "10px" },
                marginTop: { xs: "30px", sm: "20px" },
                flexDirection: {
                  xs: "column", // Empilha os botões em telas menores
                  sm: "row", // Alinha os botões na horizontal em telas maiores
                },
              }}
            >
              <Button
                variant="outlined"
                sx={{
                  padding: "0 12px",
                  minWidth: {
                    xs: "100%",
                    sm: "0",
                  },
                  height: "36px",
                  display:
                    currentStep === 1 ||
                    currentStep === 2 ||
                    currentStep === 3 ||
                    currentStep === 4
                      ? "none"
                      : "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  borderRadius: "9999px",
                  fontSize: "12px",
                  fontWeight: 500,
                  marginLeft: "auto",
                  marginTop: { xs: "20px", sm: "0" },
                }}
                onClick={() => setCurrentStep((prevStep) => prevStep - 1)}
              >
                Voltar
              </Button>
              <Button
                variant="contained"
                sx={{
                  padding: "0 12px",
                  minWidth: {
                    xs: "100%",
                    sm: "0",
                  },
                  height: "36px",
                  display:
                    currentStep === 2 ||
                    (currentStep === 3 && firstTime > 0) ||
                    currentStep === 4
                      ? "none"
                      : "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  borderRadius: "9999px",
                  fontSize: "12px",
                  fontWeight: 500,
                  marginLeft: "auto",
                  marginTop: { xs: "20px", sm: "0" },
                }}
                onClick={() => (canMoveForward() ? handleAdvance() : null)}
              >
                Próximo
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>
      <CampaignModal
        cancelText="Adicionar mais um canal"
        confirmText="Retornar ao formulário"
        subTitle={
          "Percebemos que você está configurando uma campanha. Gostaria de retornar ao formulário?"
        }
        isOpen={openModalCampaign}
        onClose={() => setOpenModalCampaign(false)}
        onCancel={() => navigate("/canais/adicionar")}
        onConfirm={() => (
          HandleChannels(), sessionStorage.setItem("ReturToCampaign", true)
        )}
        title={"Canal adicionado com sucesso"}
        iconColor={themeColors.purple400}
      />
    </Box>
  );
};

export default ChannelPageAddEmail;
